import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import Svg, { Circle, Line, Path, Polyline, Rect } from 'react-native-svg';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import type { ToolProps } from '../olmps/ToolHost';
import { GlassCard, Atmos } from '../v10/ui';
import { ScreenHelp } from '../v10/ScreenHelp';
import { argosHelp } from '../olmps/help/argosHelp';
import { useTheme, makeThemedStyles, DISPLAY_FONT, radii, series as chartSeries } from '../v10/theme';

// NOTE ON DATA SOURCE — the web reference (src/app/argos/page.tsx) reads from
// `eyes_categories` / `eyes_sessions` / `eyes_blocks` / `eyes_shopping_items`.
// Those tables do not exist in the live schema; the live ARGOS tables are
// `argos_categories` / `argos_sessions` / `argos_blocks` / `argos_shopping_items`
// (verified directly against the live PostgREST schema with both anon and
// service-role keys). This tool queries the real, live table names. None of
// these tables carry a `user_id` column — ARGOS is a single-user browser
// telemetry pipeline (fed by a Chrome extension), so no `.eq('user_id', ...)`
// filter is applied to them, matching the web page's own queries exactly.

// ── Types (mirrors web `src/app/argos/page.tsx` interfaces 1:1) ──

interface EyesCategory {
  id: string;
  name: string;
  emoji: string;
  slug: string;
  is_explicit: boolean;
}

interface EyesSession {
  id: string;
  source: string;
  category_slug: string;
  subcategory: string | null;
  domain: string;
  duration_seconds: number;
  started_at: string;
  time_of_day: string;
  day_of_week: string;
}

interface EyesBlock {
  id: string;
  source: string;
  domain: string;
  category_slug: string;
  reason: string;
  overridden: boolean;
  blocked_at: string;
}

interface ShoppingItem {
  id: string;
  product_name: string;
  product_type: string;
  price: number | null;
  currency: string;
  site: string;
  created_at: string;
}

type DateRange = 'today' | 'week' | 'month';
type MainTab = 'overview' | 'deepdive';
type DeepTab = 'shopping' | 'explicit' | 'patterns';

// Chart ramp comes from theme `series` (gold first).
function colorFor(index: number): string {
  return chartSeries[index % chartSeries.length];
}

// ── Formatting helpers ──

function formatDuration(seconds: number): string {
  if (!seconds || seconds < 60) return `${Math.max(0, Math.round(seconds))}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return remainMins > 0 ? `${hrs}h ${remainMins}m` : `${hrs}h`;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDayLabel(d: Date): string {
  return `${MONTH_ABBR[d.getMonth()]} ${d.getDate()}`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  let hours = d.getHours();
  const minutes = pad2(d.getMinutes());
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${formatDayLabel(d)}, ${hours}:${minutes} ${ampm}`;
}

function startOfDay(d: Date): Date {
  const n = new Date(d);
  n.setHours(0, 0, 0, 0);
  return n;
}

function endOfDay(d: Date): Date {
  const n = new Date(d);
  n.setHours(23, 59, 59, 999);
  return n;
}

// Monday-start week, matching the web's `startOfWeek(now, { weekStartsOn: 1 })`.
function startOfWeekMonday(d: Date): Date {
  const n = startOfDay(d);
  const day = n.getDay(); // 0 = Sunday
  const diff = day === 0 ? 6 : day - 1;
  n.setDate(n.getDate() - diff);
  return n;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}

function eachDayOfInterval(from: Date, to: Date): Date[] {
  const days: Date[] = [];
  const cursor = startOfDay(from);
  const end = startOfDay(to);
  while (cursor.getTime() <= end.getTime()) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// ── Small chart primitives (react-native-svg) ──

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function pieSlicePath(cx: number, cy: number, r: number, startAngle: number, endAngle: number): string {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y} Z`;
}

interface PieSlice {
  key: string;
  label: string;
  value: number;
  color: string;
}

function PieChartCard({ data, size = 160 }: { data: PieSlice[]; size?: number }) {
  const { c } = useTheme();
  const styles = useStyles();
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 2;

  let cursor = 0;
  const slices = data.map((d) => {
    const share = total > 0 ? d.value / total : 0;
    const startAngle = cursor * 360;
    const endAngle = (cursor + share) * 360;
    cursor += share;
    return { ...d, share, startAngle, endAngle };
  });

  const onlyOneFullSlice = slices.length === 1 && total > 0;

  return (
    <View style={styles.pieRow}>
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {total === 0 ? (
          <Circle cx={cx} cy={cy} r={r} fill="none" stroke={c.glassRim} strokeWidth={2} />
        ) : onlyOneFullSlice ? (
          <Circle cx={cx} cy={cy} r={r} fill={slices[0].color} />
        ) : (
          slices.map((s) => (
            <Path key={s.key} d={pieSlicePath(cx, cy, r, s.startAngle, s.endAngle)} fill={s.color} />
          ))
        )}
      </Svg>
      <View style={styles.legend}>
        {slices.length === 0 ? (
          <Text style={styles.legendEmpty}>No data.</Text>
        ) : (
          slices.map((s) => (
            <View key={s.key} style={styles.legendRow}>
              <View style={[styles.legendSwatch, { backgroundColor: s.color }]} />
              <Text style={styles.legendLabel} numberOfLines={1}>
                {s.label}
              </Text>
              <Text style={styles.legendValue}>{Math.round(s.share * 100)}%</Text>
            </View>
          ))
        )}
      </View>
    </View>
  );
}

interface Series {
  key: string;
  label: string;
  color: string;
}

// Stacked bar chart — one column per bucket, segments stacked per series.
function StackedBarChart({
  buckets,
  series,
  valuesByBucket,
  width,
  height = 160,
}: {
  buckets: string[];
  series: Series[];
  valuesByBucket: Record<string, Record<string, number>>;
  width: number;
  height?: number;
}) {
  const { t } = useTheme();
  const styles = useStyles();
  const totals = buckets.map((b) => {
    const row = valuesByBucket[b] ?? {};
    return series.reduce((sum, s) => sum + (row[s.key] ?? 0), 0);
  });
  const maxTotal = Math.max(1, ...totals);

  const axisWidth = 40;
  const plotWidth = Math.max(1, width - axisWidth);
  const gap = 14;
  const barWidth = Math.max(18, (plotWidth - gap * (buckets.length + 1)) / Math.max(1, buckets.length));

  const gridLines = [0, 0.25, 0.5, 0.75, 1];

  return (
    <View>
      <View style={styles.chartRow}>
        <View style={[styles.axisCol, { height }]}>
          {gridLines.map((g) => (
            <Text
              key={g}
              style={[styles.axisLabel, { position: 'absolute', bottom: g * height - 6, right: 4 }]}
            >
              {formatDuration(Math.round(maxTotal * g))}
            </Text>
          ))}
        </View>
        <Svg width={plotWidth} height={height}>
          {gridLines.map((g) => (
            <Line
              key={g}
              x1={0}
              y1={height - g * height}
              x2={plotWidth}
              y2={height - g * height}
              stroke={t.lineFaint}
              strokeWidth={1}
            />
          ))}
          {buckets.map((b, i) => {
            const row = valuesByBucket[b] ?? {};
            const x = gap + i * (barWidth + gap);
            let yOffset = height;
            return (
              <React.Fragment key={b}>
                {series.map((s) => {
                  const v = row[s.key] ?? 0;
                  if (v <= 0) return null;
                  const h = (v / maxTotal) * height;
                  const y = yOffset - h;
                  yOffset = y;
                  return <Rect key={s.key} x={x} y={y} width={barWidth} height={h} fill={s.color} />;
                })}
              </React.Fragment>
            );
          })}
        </Svg>
      </View>
      <View style={[styles.chartRow, { marginTop: 4 }]}>
        <View style={{ width: axisWidth }} />
        <View style={{ width: plotWidth, flexDirection: 'row' }}>
          {buckets.map((b, i) => (
            <View key={b} style={{ width: barWidth, marginLeft: gap, alignItems: 'center' }}>
              <Text style={styles.axisLabel} numberOfLines={1}>
                {b}
              </Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
}

// Multi-line chart — one polyline per series, values already in display units
// (e.g. minutes). Horizontally scrollable when there are many buckets.
function MultiLineChart({
  buckets,
  series,
  valuesByBucket,
  width,
  height = 160,
}: {
  buckets: string[];
  series: Series[];
  valuesByBucket: Record<string, Record<string, number>>;
  width: number;
  height?: number;
}) {
  const { t } = useTheme();
  const styles = useStyles();
  const axisWidth = 40;
  const perBucket = 44;
  const plotWidth = Math.max(width - axisWidth, buckets.length * perBucket);

  let maxValue = 0;
  buckets.forEach((b) => {
    const row = valuesByBucket[b] ?? {};
    series.forEach((s) => {
      maxValue = Math.max(maxValue, row[s.key] ?? 0);
    });
  });
  maxValue = Math.max(1, maxValue);

  const gridLines = [0, 0.25, 0.5, 0.75, 1];
  const stepX = buckets.length > 1 ? plotWidth / (buckets.length - 1) : plotWidth;

  const points = series.map((s) => {
    const coords = buckets.map((b, i) => {
      const v = (valuesByBucket[b] ?? {})[s.key] ?? 0;
      const x = buckets.length > 1 ? i * stepX : plotWidth / 2;
      const y = height - (v / maxValue) * height;
      return `${x},${y}`;
    });
    return { key: s.key, color: s.color, points: coords.join(' ') };
  });

  return (
    <View style={styles.chartRow}>
      <View style={[styles.axisCol, { height }]}>
        {gridLines.map((g) => (
          <Text
            key={g}
            style={[styles.axisLabel, { position: 'absolute', bottom: g * height - 6, right: 4 }]}
          >
            {Math.round(maxValue * g)}m
          </Text>
        ))}
      </View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View>
          <Svg width={plotWidth} height={height}>
            {gridLines.map((g) => (
              <Line
                key={g}
                x1={0}
                y1={height - g * height}
                x2={plotWidth}
                y2={height - g * height}
                stroke={t.lineFaint}
                strokeWidth={1}
              />
            ))}
            {points.map((p) => (
              <Polyline key={p.key} points={p.points} fill="none" stroke={p.color} strokeWidth={2} />
            ))}
          </Svg>
          <View style={{ flexDirection: 'row', width: plotWidth, marginTop: 4 }}>
            {buckets.map((b) => (
              <Text
                key={b}
                style={[styles.axisLabel, { width: Math.max(stepX, perBucket / 2), textAlign: 'center' }]}
                numberOfLines={1}
              >
                {b}
              </Text>
            ))}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

function ChartLegend({ series }: { series: Series[] }) {
  const styles = useStyles();
  if (series.length === 0) return null;
  return (
    <View style={styles.legendWrap}>
      {series.map((s) => (
        <View key={s.key} style={styles.legendChip}>
          <View style={[styles.legendSwatch, { backgroundColor: s.color }]} />
          <Text style={styles.legendChipText} numberOfLines={1}>
            {s.label}
          </Text>
        </View>
      ))}
    </View>
  );
}

// ── Card shell ──

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  const styles = useStyles();
  return (
    <GlassCard style={styles.card}>
      <Text style={styles.cardTitle}>{title}</Text>
      {subtitle ? <Text style={styles.cardSubtitle}>{subtitle}</Text> : null}
      <View style={{ marginTop: 10 }}>{children}</View>
    </GlassCard>
  );
}

// ── Main component ──

export function ArgosTool(_props: ToolProps) {
  const { c } = useTheme();
  const styles = useStyles();
  const { session: authSession } = useAuth();
  const userId = authSession?.user.id;
  const { width: windowWidth } = useWindowDimensions();
  const chartWidth = Math.max(220, windowWidth - 24 - 32); // panel padding + card padding

  const [dateRange, setDateRange] = useState<DateRange>('today');
  const [mainTab, setMainTab] = useState<MainTab>('overview');
  const [deepTab, setDeepTab] = useState<DeepTab>('shopping');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [categories, setCategories] = useState<EyesCategory[]>([]);
  const [sessions, setSessions] = useState<EyesSession[]>([]);
  const [blocks, setBlocks] = useState<EyesBlock[]>([]);
  const [shoppingItems, setShoppingItems] = useState<ShoppingItem[]>([]);

  const [patterns, setPatterns] = useState<string[]>([]);
  const [patternsLoading, setPatternsLoading] = useState(false);
  const [patternsError, setPatternsError] = useState<string | null>(null);

  const { from, to } = useMemo(() => {
    const now = new Date();
    switch (dateRange) {
      case 'today':
        return { from: startOfDay(now), to: endOfDay(now) };
      case 'week':
        return { from: startOfWeekMonday(now), to: endOfDay(now) };
      case 'month':
        return { from: startOfMonth(now), to: endOfDay(now) };
      default:
        return { from: startOfDay(now), to: endOfDay(now) };
    }
  }, [dateRange]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const fromISO = from.toISOString();
    const toISO = to.toISOString();

    const [catRes, sessRes, blockRes, shopRes] = await Promise.all([
      supabase.from('argos_categories').select('id, name, emoji, slug, is_explicit').eq('is_active', true).order('sort_order'),
      supabase
        .from('argos_sessions')
        .select('id, source, category_slug, subcategory, domain, duration_seconds, started_at, time_of_day, day_of_week')
        .gte('started_at', fromISO)
        .lte('started_at', toISO)
        .order('started_at', { ascending: false }),
      supabase
        .from('argos_blocks')
        .select('id, source, domain, category_slug, reason, overridden, blocked_at')
        .gte('blocked_at', fromISO)
        .lte('blocked_at', toISO)
        .order('blocked_at', { ascending: false }),
      supabase
        .from('argos_shopping_items')
        .select('id, product_name, product_type, price, currency, site, created_at')
        .gte('created_at', fromISO)
        .lte('created_at', toISO)
        .order('created_at', { ascending: false }),
    ]);

    const firstError = catRes.error || sessRes.error || blockRes.error || shopRes.error;
    if (firstError) {
      setError(firstError.message);
    } else {
      setError(null);
      setCategories((catRes.data ?? []) as EyesCategory[]);
      setSessions((sessRes.data ?? []) as EyesSession[]);
      setBlocks((blockRes.data ?? []) as EyesBlock[]);
      setShoppingItems((shopRes.data ?? []) as ShoppingItem[]);
    }
    setLoading(false);
  }, [userId, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const catMap = useMemo(() => {
    const m: Record<string, EyesCategory> = {};
    categories.forEach((c) => {
      m[c.slug] = c;
      m[c.id] = c;
    });
    return m;
  }, [categories]);

  const allCategorySlugs = useMemo(() => {
    const slugs = new Set<string>();
    sessions.forEach((s) => slugs.add(s.category_slug || 'unknown'));
    return Array.from(slugs);
  }, [sessions]);

  const categorySeries: Series[] = useMemo(
    () =>
      allCategorySlugs.map((slug, i) => ({
        key: slug,
        label: catMap[slug]?.emoji ? `${catMap[slug].emoji} ${catMap[slug].name}` : slug,
        color: colorFor(i),
      })),
    [allCategorySlugs, catMap]
  );

  const categoryBreakdown: PieSlice[] = useMemo(() => {
    const totals: Record<string, number> = {};
    sessions.forEach((s) => {
      const slug = s.category_slug || 'unknown';
      totals[slug] = (totals[slug] || 0) + s.duration_seconds;
    });
    return Object.entries(totals)
      .map(([slug, seconds]) => {
        const idx = allCategorySlugs.indexOf(slug);
        return {
          key: slug,
          label: catMap[slug]?.emoji ? `${catMap[slug].emoji} ${catMap[slug].name}` : slug,
          value: seconds,
          color: colorFor(idx >= 0 ? idx : 0),
        };
      })
      .sort((a, b) => b.value - a.value);
  }, [sessions, catMap, allCategorySlugs]);

  const TIME_PERIODS = ['morning', 'afternoon', 'evening', 'night'];

  const timeOfDayValues = useMemo(() => {
    const data: Record<string, Record<string, number>> = {};
    TIME_PERIODS.forEach((p) => {
      data[capitalize(p)] = {};
    });
    sessions.forEach((s) => {
      const period = capitalize(s.time_of_day || 'other');
      if (!data[period]) data[period] = {};
      const slug = s.category_slug || 'unknown';
      data[period][slug] = (data[period][slug] || 0) + s.duration_seconds;
    });
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  const timeOfDayBuckets = TIME_PERIODS.map(capitalize);

  const dailyTimeline = useMemo(() => {
    const days = eachDayOfInterval(from, to);
    const byDay: Record<string, Record<string, number>> = {};
    const labels: string[] = [];
    days.forEach((d) => {
      const key = ymd(d);
      byDay[key] = {};
      labels.push(formatDayLabel(d));
    });
    const keyToLabel: Record<string, string> = {};
    days.forEach((d) => {
      keyToLabel[ymd(d)] = formatDayLabel(d);
    });
    sessions.forEach((s) => {
      const key = ymd(new Date(s.started_at));
      if (!byDay[key]) return;
      const slug = s.category_slug || 'unknown';
      byDay[key][slug] = (byDay[key][slug] || 0) + s.duration_seconds;
    });
    const valuesByLabel: Record<string, Record<string, number>> = {};
    Object.entries(byDay).forEach(([key, cats]) => {
      const label = keyToLabel[key];
      valuesByLabel[label] = Object.fromEntries(
        Object.entries(cats).map(([slug, secs]) => [slug, Math.round(secs / 60)])
      );
    });
    return { labels, valuesByLabel };
  }, [sessions, from, to]);

  const sourceBreakdown: PieSlice[] = useMemo(() => {
    const totals: Record<string, number> = {};
    sessions.forEach((s) => {
      const src = s.source || 'unknown';
      totals[src] = (totals[src] || 0) + s.duration_seconds;
    });
    return Object.entries(totals).map(([source, seconds], i) => ({
      key: source,
      label: capitalize(source),
      value: seconds,
      color: colorFor(i),
    }));
  }, [sessions]);

  const topDomains = useMemo(() => {
    const domainStats: Record<string, { seconds: number; count: number; slug: string }> = {};
    sessions.forEach((s) => {
      if (!s.domain) return;
      if (!domainStats[s.domain]) domainStats[s.domain] = { seconds: 0, count: 0, slug: s.category_slug };
      domainStats[s.domain].seconds += s.duration_seconds;
      domainStats[s.domain].count += 1;
    });
    return Object.entries(domainStats)
      .map(([domain, stats]) => ({ domain, ...stats }))
      .sort((a, b) => b.seconds - a.seconds)
      .slice(0, 10);
  }, [sessions]);

  const todayBlocks = useMemo(() => {
    const todayKey = ymd(new Date());
    return blocks.filter((b) => b.blocked_at?.startsWith(todayKey)).length;
  }, [blocks]);

  const shoppingTotal = useMemo(
    () => shoppingItems.reduce((sum, item) => sum + (item.price || 0), 0),
    [shoppingItems]
  );

  const explicitStats = useMemo(() => {
    const explicitSessions = sessions.filter((s) => catMap[s.category_slug]?.is_explicit);
    const totalSeconds = explicitSessions.reduce((sum, s) => sum + s.duration_seconds, 0);
    const byTimeOfDay: Record<string, number> = {};
    explicitSessions.forEach((s) => {
      const period = s.time_of_day || 'other';
      byTimeOfDay[period] = (byTimeOfDay[period] || 0) + s.duration_seconds;
    });
    return {
      totalSeconds,
      sessionCount: explicitSessions.length,
      byTimeOfDay: Object.entries(byTimeOfDay).map(([period, seconds]) => ({
        period: capitalize(period),
        minutes: Math.round(seconds / 60),
      })),
    };
  }, [sessions, catMap]);

  const fetchPatterns = useCallback(async () => {
    setPatternsLoading(true);
    setPatternsError(null);
    try {
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data: recentSessions, error: sessErr } = await supabase
        .from('argos_sessions')
        .select('category_slug, duration_seconds, time_of_day, day_of_week, subcategory')
        .gte('started_at', sevenDaysAgo);
      if (sessErr) throw new Error(sessErr.message);

      const apiBase = process.env.EXPO_PUBLIC_API_BASE_URL;
      if (!apiBase) throw new Error('Missing EXPO_PUBLIC_API_BASE_URL');

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error('Not signed in');

      const res = await fetch(`${apiBase}/api/ai/argos-patterns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ sessions: recentSessions || [] }),
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      const json = (await res.json()) as { patterns?: string[] };
      setPatterns(json.patterns || []);
    } catch (err) {
      setPatternsError(err instanceof Error ? err.message : 'Failed to generate patterns.');
    } finally {
      setPatternsLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={c.blueText} />
      </View>
    );
  }

  return (
    <Atmos>
    <ScrollView style={styles.root} contentContainerStyle={styles.content}>
      <View style={styles.helpRow}>
        <ScreenHelp help={argosHelp} />
      </View>
      {error ? <Text style={styles.error}>{error}</Text> : null}

      {/* Header + date range */}
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>👁 A.R.G.O.S</Text>
          <Text style={styles.subtitle}>Browser activity tracking & focus enforcement</Text>
        </View>
      </View>
      <View style={styles.segmentRow}>
        {(['today', 'week', 'month'] as DateRange[]).map((r) => (
          <Pressable
            key={r}
            style={[styles.segment, dateRange === r && styles.segmentActive]}
            onPress={() => setDateRange(r)}
          >
            <Text style={[styles.segmentText, dateRange === r && styles.segmentTextActive]}>
              {r === 'today' ? 'Today' : r === 'week' ? 'This Week' : 'This Month'}
            </Text>
          </Pressable>
        ))}
      </View>

      {/* Main tabs */}
      <View style={styles.segmentRow}>
        {(['overview', 'deepdive'] as MainTab[]).map((t) => (
          <Pressable
            key={t}
            style={[styles.segment, mainTab === t && styles.segmentActive]}
            onPress={() => setMainTab(t)}
          >
            <Text style={[styles.segmentText, mainTab === t && styles.segmentTextActive]}>
              {t === 'overview' ? 'Overview' : 'Deep Dive'}
            </Text>
          </Pressable>
        ))}
      </View>

      {mainTab === 'overview' ? (
        sessions.length === 0 ? (
          <GlassCard style={styles.emptyCard}>
            <Text style={styles.emptyIcon}>🚫👁</Text>
            <Text style={styles.emptyTitle}>No activity data for this period.</Text>
            <Text style={styles.emptyHint}>Install the ARGOS Chrome extension to start tracking.</Text>
          </GlassCard>
        ) : (
          <>
            <Card title="Activity by Category" subtitle="Time distribution across categories">
              <PieChartCard data={categoryBreakdown} />
            </Card>

            <Card title="Time of Day" subtitle="Activity volume by period, stacked by category">
              <StackedBarChart
                buckets={timeOfDayBuckets}
                series={categorySeries}
                valuesByBucket={timeOfDayValues}
                width={chartWidth}
              />
              <ChartLegend series={categorySeries} />
            </Card>

            <Card title="Daily Timeline" subtitle="Activity minutes per day by category">
              <MultiLineChart
                buckets={dailyTimeline.labels}
                series={categorySeries}
                valuesByBucket={dailyTimeline.valuesByLabel}
                width={chartWidth}
              />
              <ChartLegend series={categorySeries} />
            </Card>

            <Card title="Sources">
              <PieChartCard data={sourceBreakdown} size={130} />
            </Card>

            <Card title="Top Domains">
              {topDomains.length === 0 ? (
                <Text style={styles.empty}>No domain data.</Text>
              ) : (
                <View style={styles.list}>
                  {topDomains.map((d) => (
                    <View key={d.domain} style={styles.rowItem}>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.rowTitle} numberOfLines={1}>
                          {d.domain}
                        </Text>
                        <Text style={styles.rowSub} numberOfLines={1}>
                          {catMap[d.slug]?.emoji} {catMap[d.slug]?.name || d.slug}
                        </Text>
                      </View>
                      <View style={{ alignItems: 'flex-end' }}>
                        <Text style={styles.rowValue}>{formatDuration(d.seconds)}</Text>
                        <Text style={styles.rowSub}>{d.count} sessions</Text>
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </Card>

            <Card title="🛡 Block Events" subtitle={`${todayBlocks} blocks today · ${blocks.length} in selected range`}>
              {blocks.length === 0 ? (
                <Text style={styles.empty}>No blocks recorded.</Text>
              ) : (
                <View style={styles.list}>
                  {blocks.slice(0, 20).map((b) => (
                    <View key={b.id} style={styles.rowItem}>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.rowTitle} numberOfLines={1}>
                          {b.domain}
                        </Text>
                        <View style={styles.badgeRow}>
                          <View style={styles.badgeDanger}>
                            <Text style={styles.badgeDangerText}>{b.reason?.replace(/_/g, ' ')}</Text>
                          </View>
                          {b.overridden ? (
                            <View style={styles.badgeOutline}>
                              <Text style={styles.badgeOutlineText}>overridden</Text>
                            </View>
                          ) : null}
                        </View>
                      </View>
                      <Text style={styles.rowSub}>{formatDateTime(b.blocked_at)}</Text>
                    </View>
                  ))}
                </View>
              )}
            </Card>
          </>
        )
      ) : (
        <>
          <View style={styles.segmentRow}>
            {(['shopping', 'explicit', 'patterns'] as DeepTab[]).map((t) => (
              <Pressable
                key={t}
                style={[styles.segment, deepTab === t && styles.segmentActive]}
                onPress={() => setDeepTab(t)}
              >
                <Text style={[styles.segmentText, deepTab === t && styles.segmentTextActive]}>
                  {t === 'shopping' ? '🛒 Shopping' : t === 'explicit' ? '🙈 Extra' : '🧠 Patterns'}
                </Text>
              </Pressable>
            ))}
          </View>

          {deepTab === 'shopping' ? (
            <Card
              title="Shopping Activity"
              subtitle={`Estimated spend this period: $${shoppingTotal.toFixed(2)}`}
            >
              {shoppingItems.length === 0 ? (
                <Text style={styles.empty}>No shopping items tracked in this period.</Text>
              ) : (
                <View style={styles.list}>
                  {shoppingItems.map((item) => (
                    <View key={item.id} style={styles.shopRow}>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.rowTitle} numberOfLines={1}>
                          {item.product_name}
                        </Text>
                        <View style={styles.badgeRow}>
                          <View style={styles.badgeOutline}>
                            <Text style={styles.badgeOutlineText}>{item.product_type}</Text>
                          </View>
                          <Text style={styles.rowSub}>{item.site}</Text>
                        </View>
                      </View>
                      <View style={{ alignItems: 'flex-end' }}>
                        <Text style={styles.rowValue}>
                          {item.currency} {(item.price ?? 0).toFixed(2)}
                        </Text>
                        <Text style={styles.rowSub}>
                          {item.created_at ? formatDayLabel(new Date(item.created_at)) : ''}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </Card>
          ) : null}

          {deepTab === 'explicit' ? (
            <Card title="🙈 Extra Content — Aggregates Only" subtitle="Privacy-protected. No domains or titles are shown.">
              <View style={styles.summaryRow}>
                <GlassCard style={styles.summaryCard}>
                  <Text style={styles.summaryValue}>{formatDuration(explicitStats.totalSeconds)}</Text>
                  <Text style={styles.summaryLabel}>Total Duration</Text>
                </GlassCard>
                <GlassCard style={styles.summaryCard}>
                  <Text style={styles.summaryValue}>{explicitStats.sessionCount}</Text>
                  <Text style={styles.summaryLabel}>Sessions</Text>
                </GlassCard>
              </View>
              {explicitStats.byTimeOfDay.length > 0 ? (
                <View style={{ marginTop: 14 }}>
                  <Text style={styles.sectionTitle}>Time of Day Breakdown</Text>
                  <View style={{ marginTop: 6, gap: 6 }}>
                    {explicitStats.byTimeOfDay.map(({ period, minutes }) => (
                      <View key={period} style={styles.kvRow}>
                        <Text style={styles.kvKey}>{period}</Text>
                        <Text style={styles.kvValue}>{minutes}m</Text>
                      </View>
                    ))}
                  </View>
                </View>
              ) : null}
            </Card>
          ) : null}

          {deepTab === 'patterns' ? (
            <Card
              title="🧠 AI Pattern Analysis"
              subtitle="Powered by Claude. Analyses last 7 days of ARGOS activity. No explicit domains are shared."
            >
              {patternsError ? <Text style={styles.error}>{patternsError}</Text> : null}
              {patterns.length === 0 && !patternsLoading ? (
                <Pressable style={styles.outlineButton} onPress={() => void fetchPatterns()}>
                  <Text style={styles.outlineButtonText}>Generate Patterns</Text>
                </Pressable>
              ) : null}
              {patternsLoading ? (
                <View style={styles.loadingRow}>
                  <ActivityIndicator color={c.blueText} />
                  <Text style={styles.loadingText}>Analysing patterns...</Text>
                </View>
              ) : null}
              {patterns.length > 0 ? (
                <View style={{ gap: 8 }}>
                  {patterns.map((p, i) => (
                    <GlassCard key={i} style={styles.patternCard}>
                      <Text style={styles.patternText}>{p}</Text>
                    </GlassCard>
                  ))}
                  <Pressable style={styles.ghostButton} onPress={() => void fetchPatterns()}>
                    <Text style={styles.ghostButtonText}>Regenerate</Text>
                  </Pressable>
                </View>
              ) : null}
            </Card>
          ) : null}
        </>
      )}
    </ScrollView>
    </Atmos>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  root: { flex: 1 },
  content: { padding: 12, gap: 12, paddingBottom: 24 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  error: { color: c.danger, fontSize: 13, marginBottom: 4 },
  helpRow: { flexDirection: 'row', justifyContent: 'flex-end', marginBottom: -4 },

  header: { gap: 2 },
  title: { fontSize: 20, fontWeight: '900', color: c.blueText, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  subtitle: { fontSize: 12, color: c.slate },

  segmentRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  segment: {
    flex: 1,
    minWidth: 90,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: radii.pill,
    backgroundColor: c.glass,
    alignItems: 'center',
    ...neu.raisedSm,
  },
  segmentActive: { backgroundColor: c.primary, ...neu.glowPrimary },
  segmentText: { fontSize: 12, fontWeight: '700', color: c.slate },
  segmentTextActive: { color: t.onGold },

  emptyCard: {
    borderRadius: radii.tile,
    padding: 28,
    alignItems: 'center',
    gap: 6,
  },
  emptyIcon: { fontSize: 32, marginBottom: 4 },
  emptyTitle: { fontSize: 15, fontWeight: '700', color: c.text, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  emptyHint: { fontSize: 12, color: c.slate },

  card: { borderRadius: radii.tile, padding: 14 },
  cardTitle: { fontSize: 15, fontWeight: '800', color: c.text, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  cardSubtitle: { fontSize: 12, color: c.slate, marginTop: 2 },

  pieRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  legend: { flex: 1, gap: 6 },
  legendRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  legendSwatch: { width: 10, height: 10, borderRadius: 3 },
  legendLabel: { flex: 1, fontSize: 12, color: c.text },
  legendValue: { fontSize: 12, fontWeight: '700', color: c.blueText },
  legendEmpty: { fontSize: 12, color: c.slate, fontStyle: 'italic' },

  legendWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  legendChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: c.glass,
    borderRadius: radii.pill,
    paddingVertical: 4,
    paddingHorizontal: 8,
    maxWidth: 160,
    ...neu.raisedSm,
  },
  legendChipText: { fontSize: 11, color: c.text },

  chartRow: { flexDirection: 'row' },
  axisCol: { width: 40, position: 'relative' },
  axisLabel: { fontSize: 10, color: c.slate },

  list: { gap: 8 },
  rowItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: t.lineFaint,
  },
  shopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 8,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: t.lineFaint,
  },
  rowTitle: { fontSize: 13, fontWeight: '700', color: c.text, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  rowSub: { fontSize: 11, color: c.slate, marginTop: 2 },
  rowValue: { fontSize: 13, fontWeight: '800', color: c.blueText },

  badgeRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' },
  badgeDanger: {
    backgroundColor: c.lime,
    borderRadius: radii.pill,
    paddingVertical: 2,
    paddingHorizontal: 8,
  },
  badgeDangerText: { fontSize: 10, fontWeight: '700', color: c.danger, textTransform: 'capitalize' },
  badgeOutline: {
    backgroundColor: c.glass,
    borderRadius: radii.pill,
    paddingVertical: 2,
    paddingHorizontal: 8,
    ...neu.raisedSm,
  },
  badgeOutlineText: { fontSize: 10, fontWeight: '700', color: c.slate },

  empty: { color: c.slate, fontStyle: 'italic', fontSize: 13 },

  summaryRow: { flexDirection: 'row', gap: 8 },
  summaryCard: {
    flex: 1,
    borderRadius: radii.tile,
    padding: 14,
    alignItems: 'center',
    gap: 4,
  },
  summaryValue: { fontSize: 20, fontWeight: '900', color: c.text },
  summaryLabel: { fontSize: 11, color: c.slate },

  sectionTitle: { fontSize: 13, fontWeight: '800', color: c.text, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  kvRow: { flexDirection: 'row', justifyContent: 'space-between' },
  kvKey: { fontSize: 13, color: c.text, textTransform: 'capitalize' },
  kvValue: { fontSize: 13, fontWeight: '700', color: c.text },

  outlineButton: {
    backgroundColor: c.lime,
    borderRadius: radii.pill,
    paddingVertical: 10,
    alignItems: 'center',
    ...neu.inset,
  },
  outlineButtonText: { fontSize: 13, fontWeight: '700', color: c.blueText },
  loadingRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  loadingText: { fontSize: 13, color: c.slate },
  patternCard: {
    borderRadius: radii.tile,
    padding: 12,
    ...neu.raised,
  },
  patternText: { fontSize: 13, color: c.text, lineHeight: 18 },
  ghostButton: { alignItems: 'center', paddingVertical: 8 },
  ghostButtonText: { fontSize: 12, fontWeight: '700', color: c.blueText },
}),
);
